<div class="flex flex-col gap-(--space-4) @container/table">
  <div class="flex justify-between items-center">
    <div class="flex gap-(--space-2)">
      @if (canWrite()) {
        <ces-button-secondary
          icon="checklist"
          [label]="'common.dialogs.selectMeasures.title' | transloco"
          (onClick)="openSelectMeasuresDialog()" />
      }
    </div>
    @if (canWrite()) {
      <ces-button
        [label]="'common.buttons.save' | transloco"
        [disabled]="!isDirty()"
        [pTooltip]="!isDirty() ? ('common.tooltips.noChanges' | transloco) : ''"
        tooltipPosition="bottom"
        (onClick)="onSaveClick()" />
    }
  </div>

  @if (isLoading()) {
    <ces-grid-skeleton
      [lines]="5"
      [columns]="['', '', '', '', '', '', '', '', '']"
      [widths]="['3rem', '3rem', '', '8rem', '8rem', '8rem', '7rem', '10rem', '4rem']"
      [heights]="['1.5rem', '1.5rem', '1.5rem', '1.5rem', '1.5rem', '1.5rem', '2rem', '2rem', '1.5rem']" />
  } @else if (measures().length === 0) {
    <div class="flex flex-col items-center justify-center py-(--space-8) text-center bg-(color:--p-surface-50) rounded-(--radius-md)">
      <i class="pi pi-list text-4xl text-(color:--p-surface-400) mb-(--space-2)"></i>
      <p class="text-(color:--text-secondary)">{{
        'tables.config.emptyState' | transloco: { buttonLabel: ('common.dialogs.selectMeasures.title' | transloco) }
      }}</p>
    </div>
  } @else {
    <p-table
      [value]="measures()"
      styleClass="p-datatable-sm"
      [rowTrackBy]="trackByMeasureId"
      (onRowReorder)="onRowReorder($event)"
      dataKey="measureId">
      <ng-template #header>
        <tr>
          @if (canWrite()) {
            <th class="w-12"></th>
            <th class="w-8">
              <p-checkbox
                [binary]="true"
                [ngModel]="allSelected()"
                [indeterminate]="hasSelection() && !allSelected()"
                (ngModelChange)="toggleSelectAll($event)"
                [attr.aria-label]="'common.accessibility.selectAll' | transloco" />
            </th>
          }
          <th class="whitespace-nowrap">{{ 'tables.config.columns.name' | transloco }}</th>
          @for (col of booleanColumns; track col.i18nKey) {
            <th
              class="w-50 text-center whitespace-nowrap @max-[1280px]/table:whitespace-normal @max-[1280px]/table:[&>span]:min-h-[2lh]">
              <span class="inline-flex items-center justify-center">{{ 'tables.config.columns.' + col.i18nKey | transloco }}</span>
            </th>
          }
          <th class="w-30">{{ 'tables.config.columns.decimals' | transloco }}</th>
          <th class="w-30">
            <span class="inline-flex items-center gap-1">
              {{ 'tables.config.columns.unit' | transloco }}
              <span
                class="material-symbols fill text-(color:--p-gray-300) text-[20px] cursor-help leading-none"
                [pTooltip]="'tables.config.unitTooltip' | transloco"
                tooltipPosition="top"
                >info</span
              >
            </span>
          </th>
          @if (canWrite()) {
            <th class="w-16"></th>
          }
        </tr>
        @if (canWrite()) {
          <tr
            class="[&>th]:bg-(color:--p-gray-100) [&>th]:py-1 [&>th]:px-[7.5px] [&>th:first-child]:rounded-l [&>th:last-child]:rounded-r">
            <th></th>
            <th></th>
            <th class="text-(color:--p-gray-300) font-normal text-[13px] leading-4">{{ 'tables.config.applyToAll' | transloco }}</th>
            @for (col of booleanColumns; track col.i18nKey) {
              <th class="text-center">
                <p-toggleswitch
                  [ngModel]="col.global()"
                  (ngModelChange)="col.applyGlobal($event)"
                  [ariaLabel]="col.globalAriaKey | transloco" />
              </th>
            }
            <th>
              <p-select
                [options]="decimalsOptions"
                [ngModel]="globalDecimals()"
                (ngModelChange)="applyGlobalDecimals($event)"
                optionLabel="label"
                optionValue="value"
                appendTo="body"
                styleClass="w-full"
                [placeholder]="globalDecimalsIndeterminate() ? '—' : ''"
                [attr.aria-label]="'tables.config.globalDecimals' | transloco" />
            </th>
            <th></th>
            <th></th>
          </tr>
        }
      </ng-template>
      <ng-template #body let-measure let-index="rowIndex">
        <tr [pReorderableRow]="index">
          @if (canWrite()) {
            <td>
              <span
                class="pi pi-bars cursor-move inline-flex items-center align-middle"
                pReorderableRowHandle
                role="button"
                [attr.aria-label]="'common.accessibility.reorderRow' | transloco: { name: measure.measureName }"></span>
            </td>
            <td>
              <p-checkbox
                [binary]="true"
                [ngModel]="measure.selected"
                (ngModelChange)="toggleSelection(measure, $event)"
                [attr.aria-label]="'common.accessibility.selectRow' | transloco: { name: measure.measureName }" />
            </td>
          }
          <td>
            @if (canReadMeasures()) {
              <a
                [href]="getMeasureLink(measure)"
                target="_blank"
                rel="noopener noreferrer"
                class="underline text-[var(--p-text-color)] hover:text-[var(--p-primary-color)] cursor-pointer">
                {{ measure.measureName }}
              </a>
            } @else {
              <span>{{ measure.measureName }}</span>
            }
          </td>
          @for (col of booleanColumns; track col.i18nKey) {
            <td class="text-center">
              <p-toggleswitch
                [ngModel]="measure.config[col.configKey]"
                [disabled]="!canWrite() || col.isRowDisabled(measure)"
                [pTooltip]="col.isRowDisabled(measure) && col.disabledTooltipKey ? (col.disabledTooltipKey | transloco) : ''"
                tooltipPosition="top"
                [ariaLabel]="('tables.config.columns.' + col.i18nKey | transloco) + ' — ' + measure.measureName"
                (ngModelChange)="updateConfig(measure, col.configKey, $event)" />
            </td>
          }
          <td>
            <p-select
              [options]="decimalsOptions"
              [ngModel]="measure.config.decimals"
              (ngModelChange)="updateConfig(measure, 'decimals', $event)"
              optionLabel="label"
              optionValue="value"
              appendTo="body"
              styleClass="w-full"
              [disabled]="!canWrite()" />
          </td>
          <td>
            <p-select
              [options]="measure.unitOptions"
              [ngModel]="measure.config.unitId || measure.measureUnitId"
              (ngModelChange)="updateConfig(measure, 'unitId', $event)"
              optionLabel="label"
              optionValue="value"
              appendTo="body"
              styleClass="w-full"
              [disabled]="!canWrite()" />
          </td>
          @if (canWrite()) {
            <td class="text-center">
              <ces-delete-button (onClick)="deleteMeasure(measure)" />
            </td>
          }
        </tr>
      </ng-template>
    </p-table>
  }

  @if (canWrite() && hasSelection()) {
    <ces-floating-bar>
      <span class="text-sm text-(color:--text-secondary)">
        {{ 'tables.config.measuresSelected' | transloco: { count: selectedCount() } }}
      </span>
      <ces-button-secondary
        icon="keyboard_double_arrow_up"
        size="small"
        [disabled]="!canMoveUp()"
        [tooltip]="'tables.config.moveToTop' | transloco"
        (onClick)="moveToTop()" />
      <ces-button-secondary
        icon="keyboard_arrow_up"
        size="small"
        [disabled]="!canMoveUp()"
        [tooltip]="'tables.config.moveUp' | transloco"
        (onClick)="moveUp()" />
      <ces-button-secondary
        icon="keyboard_arrow_down"
        size="small"
        [disabled]="!canMoveDown()"
        [tooltip]="'tables.config.moveDown' | transloco"
        (onClick)="moveDown()" />
      <ces-button-secondary
        icon="keyboard_double_arrow_down"
        size="small"
        [disabled]="!canMoveDown()"
        [tooltip]="'tables.config.moveToBottom' | transloco"
        (onClick)="moveToBottom()" />
      <ces-delete-button [label]="'common.buttons.delete' | transloco" (onClick)="deleteSelected()" />
      <ces-button-tertiary icon="close" [tooltip]="'common.buttons.close' | transloco" (onClick)="clearSelection()" />
    </ces-floating-bar>
  }
</div>
